import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { NavLink, useNavigate, useSearchParams } from 'react-router-dom';
import classNames from 'classnames';
import Slider from 'react-slick';
import { AuthState, IMSubscriptionPlan } from "@/types";
import LoaderIcon from "@/images/icons/Loader";
import useSeamlessLogin from '@/hooks/useSeamlessLogin';
import useSubscriptionPlans from '@/hooks/useSubscriptionPlans';
import AnnualUpgradeModal from '@/components/AnnualUpgradeModal';
import ErrorModal from '@/components/ErrorModal';
import { priceView, requestCheckoutSession } from '@/utils/utils';
// @ts-ignore
import { ReactComponent as ReactLogoColored } from '@/images/logo.svg';
import { loadStripe } from '@stripe/stripe-js';
import BillingPeriodToggle from '@/components/Subscription/BillingPeriodToggle';
import PlanSummary from '@/components/Subscription/PlanSummary';
import PlanCard from '@/components/Subscription/PlanCard';
import { filterIMPlans, getIMMonthlyEquivalent, navigateToSelectedPlan } from '@/utils/planUtils';
import ChevronLeft from '@/images/icons/ChevronLeft';
import { PrevArrow, NextArrow } from '@/components/Subscription/SliderArrows';

const JoinNew = () => {
	const auth = useSelector((state: { auth: AuthState }) => state.auth);
	const {
		isLoading: isPlansLoading,
		subscription,
		plans: fetchedPlans,
		planPeriod,
		setPlanPeriod,
		trialEndDate,
	} = useSubscriptionPlans(auth);

	const {
		telegramUser,
		loggedIn,
	} = useSeamlessLogin();

	const [searchParams, setSearchParams] = useSearchParams();
	const navigate = useNavigate();
	const sliderRef = useRef<Slider>(null);

	const [upgradeAutomatically, setUpgradeAutomatically] = useState<boolean>(true);
	const [annualUpgradeModalOpen, setAnnualUpgradeModalOpen] = useState<boolean>(false);
	const [errorModalOpen, setErrorModalOpen] = useState<boolean>(false);
	const [errorMessage, setErrorMessage] = useState<string>('');
	const [changePlanLoading, setChangePlanLoading] = useState<boolean>(false);
	const [selectedPlan, setSelectedPlan] = useState<IMSubscriptionPlan | null>(null);
	const [initialPlanNotFound, setInitialPlanNotFound] = useState<boolean>(false);
	const [showMobileSummary, setShowMobileSummary] = useState<boolean>(false);

	const [showPlanSelection, setShowPlanSelection] = useState<boolean>(false);

	const plans = fetchedPlans || [];

	useEffect(() => {
		// @ts-ignore
		if (!isPlansLoading && (subscription && (Object.keys(subscription).length > 0 && subscription?.stripeSubscription?.status !== "canceled"))) {
			navigate('/manage-subscription', { replace: true });
		}
	}, [isPlansLoading, subscription]);

	useEffect(() => {
		const billedParam = searchParams.get('billed');
		if (billedParam) {
			const initialPeriod = billedParam === 'yearly' ? 'year' : 'month';

			setPlanPeriod(initialPeriod);
		}

		const planParam = searchParams.get('plan');
		const periodParam = searchParams.get('billed');

		if (!planParam || !periodParam) {
			setShowPlanSelection(true);
		}

		if (!loggedIn) {
			const redirectUrl = encodeURIComponent(location.pathname + location.search);
			navigate(`/signup?redirectUrl=${redirectUrl}`, { replace: true });
			return;
		}
	}, []);

	// When plans load, try to select the requested plan or the first available one
	useEffect(() => {
		// @ts-ignore
		if (plans && plans.length > 0 && !isPlansLoading && (!subscription || Object.keys(subscription).length === 0 || subscription?.stripeSubscription?.status === "canceled")) {
			const planName = searchParams.get('plan');
			const billedParam = searchParams.get('billed');
			const urlPeriod = billedParam === 'yearly'
				? 'year'
				: (billedParam === 'monthly' ? 'month' : planPeriod);

			const requestedPlan = plans.find((p: IMSubscriptionPlan) =>
				p.name.toLowerCase() === planName?.toLowerCase() &&
				p.period === urlPeriod
			);

			if (requestedPlan) {
				setSelectedPlan(requestedPlan);
			} else {
				// Plan not found or not suitable for user
				setInitialPlanNotFound(true);
				setShowPlanSelection(true);
				if (planName) {
					setErrorMessage("The selected plan doesn't fit your needs. Please choose from the plans below.");
					setErrorModalOpen(true);
				}

				if (plans.length > 0) {
					const filteredPlans = filterIMPlans(plans, urlPeriod, undefined);
					if (filteredPlans.length > 0) {
						const firstPlan = filteredPlans[0];

						setSelectedPlan(firstPlan);
						updateSearchParams(firstPlan, urlPeriod);
					} else {
						// No plans in the requested period, try the other period
						const alternativePeriod = urlPeriod === 'month' ? 'year' : 'month';
						const alternativePlans = filterIMPlans(plans, alternativePeriod, undefined);
						if (alternativePlans.length > 0) {
							const firstAltPlan = alternativePlans[0];
							setSelectedPlan(firstAltPlan);
							setPlanPeriod(alternativePeriod);
							updateSearchParams(firstAltPlan, alternativePeriod);
						}
					}
				}
			}
		}
	}, [plans, isPlansLoading, subscription]);

	useEffect(() => {
		if (!isPlansLoading && plans.length > 0 && selectedPlan) {
			const equivalentPlan = plans.find((p: IMSubscriptionPlan) =>
				p.period === planPeriod && p.name === selectedPlan.name
			);

			if (equivalentPlan) {
				setSelectedPlan(equivalentPlan);
				updateSearchParams(equivalentPlan, planPeriod);
				navigateToSelectedPlan(equivalentPlan, filteredPlans, sliderRef, sliderSettings);
			} else {
				// No equivalent plan in the new period, find the first available
				const filteredPlans = filterIMPlans(plans, planPeriod, undefined);
				if (filteredPlans.length > 0) {
					setSelectedPlan(filteredPlans[0]);
					updateSearchParams(filteredPlans[0], planPeriod);
				} else {
					// Switch back to original period if no plans available
					setPlanPeriod(selectedPlan.period);
				}
			}
		}
	}, [planPeriod]);

	useEffect(() => {
		if (selectedPlan && !showPlanSelection) {
			updateSearchParams(selectedPlan, planPeriod);
		}
	}, [selectedPlan, showPlanSelection]);

	const updateSearchParams = (plan: IMSubscriptionPlan, period: string) => {
		if (!plan) return;

		const newSearchParams = new URLSearchParams(searchParams);

		newSearchParams.set('plan', plan.name.toLowerCase());
		newSearchParams.set('billed', period === 'year' ? 'yearly' : 'monthly');
		setSearchParams(newSearchParams, { replace: true });
	};

	const onRequestCheckoutSession = async (plan: IMSubscriptionPlan | null) => {
		setChangePlanLoading(true);
		if (plan) {
			let { name, period } = plan;
			let toRedirect = false;

			try {
				const response = await requestCheckoutSession({
					endpoint: `${process.env.REACT_APP_API_URL}/stripeCheckoutSession`,
					name: name,
					isYearly: period === 'year',
					region: 'CY',
					telegramUser,
				});

				if (response.ok && response.status === 200) {
					const responseData = await response.json();

					if (responseData.sessionId) {
						let stripePublicKey = responseData.stripePublicKey || process.env.REACT_APP_STRIPE_KEY;
						const stripe = await loadStripe(stripePublicKey);

						if (stripe) {
							const result = await stripe.redirectToCheckout({
								sessionId: responseData.sessionId,
							});

							if (result.error) {
								console.log(`Stripe: ${result.error.message}`);
								toRedirect = true;
							}
						} else {
							console.log("There was an issue processing your payment. Please try again later.");
							toRedirect = true;
						}
					} else {
						toRedirect = true;
					}
				}
			} catch (error) {
				console.log(error);
				toRedirect = true;
			} finally {
				setChangePlanLoading(false);
			}

			if (toRedirect) {
				navigate('/manage-subscription');
			}
		}
	};

	const submitCheckout = () => {
		if (planPeriod === 'month') {
			setAnnualUpgradeModalOpen(true);
		} else {
			onRequestCheckoutSession(selectedPlan);
		}
	};

	const getMonthlyEquivalent = (yearlyPlan: IMSubscriptionPlan): IMSubscriptionPlan | null => {
		if (!plans) return null;

		return getIMMonthlyEquivalent(yearlyPlan, plans);
	};

	const sliderSettings = {
		dots: false,
		infinite: false,
		speed: 500,
		slidesToShow: 3,
		slidesToScroll: 1,
		arrows: true,
		prevArrow: <PrevArrow />,
		nextArrow: <NextArrow />
	};

	const filteredPlans = filterIMPlans(plans, planPeriod, undefined);

	if (isPlansLoading) {
		return (
			<div className="flex items-center justify-center py-1 pointer-events-none w-full h-screen">
				<LoaderIcon className="animate-rotation w-10 h-10 text-brand-green" />
			</div>
		);
	}

	return (
		<div className="flex flex-col lg:flex-row h-screen">
			<div className={`bg-brand-white w-full lg:w-[73%] overflow-y-auto ${showMobileSummary ? 'hidden lg:block' : 'block'}`}>
				<div className="lg:min-h-screen text-[#4a4a4a]">
					<div className="flex md:justify-center">
						<NavLink to="https://invitemember.com/" className="w-60 md:w-auto inline-flex p-4 md:px-4 md:py-4 md:mt-6 md:ml-6 mb-4 hover:opacity-80 transition duration-300 ease-out mr-auto">
							<ReactLogoColored className="w-full" />
						</NavLink>
					</div>
					<section className="flex items-stretch flex-col justify-between text-brand-green lg:min-h-[calc(100%_-104px)] relative">
						<div className={classNames(
							'h-full pt-6 md:pt-14 px-4 md:px-6 xl:px-8',
							showPlanSelection ? 'pb-28 md:pb-14 ' : 'pb-4 lg:pb-14',
						)}>
							<div className="basis-0 grow shrink">
								<div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 lg:gap-4">
									<div className="max-w-[400px]">
										<h1 className="h1 text-3xl/[0.9] md:text-4xl/[0.9] lg:text-[4rem]/[0.8] mt-0 mb-0">
											{showPlanSelection ? "Choose your plan" : "Your plan"}
										</h1>
									</div>
									<div className="flex items-center gap-3 lg:translate-y-1">
										<div className="flex justify-center md:mb-3">
											<BillingPeriodToggle
												period={planPeriod as 'month' | 'year'}
												onToggle={setPlanPeriod}
												className="lg:translate-y-1"
											/>
										</div>
									</div>
								</div>

								<div className="mt-8 md:mt-12">
									{showPlanSelection ? (
										<div className="slider-container relative">
											{/* Desktop view with slider (lg screens and up) */}
											<div className="hidden lg:block">
												<Slider ref={sliderRef} {...sliderSettings} className="pt-16 relative">
													{filteredPlans.map((plan: IMSubscriptionPlan, index: number) => (
														<div key={`plan-desktop-${plan.id}-${index}`}
														     className={classNames(
															     'border rounded-[18px] p-5 xl:p-6 group transition-colors duration-300 ease-out relative h-full outline-0',
															     selectedPlan?.id === plan.id ? 'bg-[#FFFEDD] border-[#EAE9C2]' : 'bg-brand-white border-[#DFD8CD]',
														     )}
														     onClick={() => {
															     setSelectedPlan(plan);
															     updateSearchParams(plan, planPeriod);
														     }}>
															<PlanCard
																plan={plan}
																selectedPlanId={selectedPlan?.id}
																getMonthlyEquivalent={getMonthlyEquivalent}
																expandable={true}
															/>
														</div>
													))}
												</Slider>
											</div>

											{/* Mobile/tablet view with stacked cards (less than lg screens) */}
											<div className="lg:hidden space-y-4">
												{filteredPlans.map((plan: IMSubscriptionPlan, index: number) => (
													<div key={`plan-mobile-${plan.id}-${index}`}
													     className={classNames(
														     'border rounded-[18px] py-4 px-5 md:p-6 group transition-colors duration-300 ease-out relative',
														     selectedPlan?.id === plan.id ? 'bg-[#FFFEDD] border-[#EAE9C2]' : 'bg-brand-white border-[#DFD8CD]',
													     )}
													     onClick={() => {
														     setSelectedPlan(plan);
														     updateSearchParams(plan, planPeriod);
													     }}>
														<PlanCard
															plan={plan}
															selectedPlanId={selectedPlan?.id}
															getMonthlyEquivalent={getMonthlyEquivalent}
															expandable={true}
														/>
													</div>
												))}
											</div>
										</div>
									) : (
										selectedPlan && (
											<div className="border rounded-[14px] md:rounded-[18px] p-5 md:py-6 md:px-8 group transition-colors relative h-full outline-0 lg:mb-4">
												<div className="flex items-center md:flex-row-reverse md:justify-between md:items-start">
													<div className="flex flex-col items-start w-full">
														<div className="font-founders text-[28px]/[0.9] lg:text-4xl/[0.9] mb-2.5 md:mb-4">{selectedPlan.name}</div>
														<div className="text-[13px]/[1.2] lg:text-[20px]/[1.15] mb-1 md:mb-2">
															{selectedPlan.salesLimit === -1 ? 'Unlimited sales' : `Sales up to ${priceView(selectedPlan.salesLimit, 'USD').replace(/\.00$/, '')}/mo`}
														</div>
													</div>
												</div>

												{initialPlanNotFound && (
													<button
														onClick={() => setShowPlanSelection(true)}
														className="mt-4 text-brand-green underline text-sm font-medium">
														Choose another plan
													</button>
												)}
											</div>
										)
									)}
								</div>
							</div>
						</div>
					</section>
				</div>
			</div>

			{/* Mobile Continue button at the bottom */}
			{selectedPlan && !showMobileSummary && showPlanSelection && (
				<div className="lg:hidden fixed bottom-0 left-0 right-0 bg-brand-white p-4">
					<button
						onClick={() => setShowMobileSummary(true)}
						className="btn-primary font-founders text-2xl/[0.7] w-full h-12 pt-2 pb-2.5 outline-0"
					>
						<span>Continue</span>
					</button>
				</div>
			)}

			{/* Mobile summary view */}
			<div className={`w-full overflow-y-auto bg-brand-white lg:hidden ${showMobileSummary ? 'block' : 'hidden'}`}>
				<div className="min-h-screen flex flex-col">
					<div
						className="flex items-center gap-1 mt-8 mb-3 ml-3 group cursor-pointer"
						onClick={() => setShowMobileSummary(false)}
					>
						<ChevronLeft className="w-5 h-5 rounded-full text-brand-green" />
						<span className="text-[11px]/[1.1] md:text-[14px]/[1.3] group-hover:translate-x-1 transition-transform duration-300">
							{showPlanSelection ? "Choose plan" : "Your plan"}
						</span>
					</div>

					<div className="flex flex-col flex-1 p-4">
						<div className="font-founders text-3xl/[0.9] mb-6">Your subscription</div>

						{selectedPlan && (
							<>
								<div className="bg-[#F7F6F2] py-5 px-4 rounded-[14px] mb-6">
									<PlanSummary plan={selectedPlan} trialEndDate={trialEndDate} />
								</div>
								<button
									onClick={() => submitCheckout()}
									className="btn-primary font-founders text-2xl/[0.7] w-full h-12 pt-2 pb-2.5 mt-auto mb-8 outline-0"
								>
									{changePlanLoading ? (
										<div className="flex items-center justify-center py-1 pointer-events-none">
											<LoaderIcon className="animate-rotation w-6 h-6 text-white" />
										</div>
									) : (
										<span>Go to checkout</span>
									)}
								</button>
							</>
						)}
					</div>
				</div>
			</div>

			<div className={`px-4 md:px-6 lg:px-0 w-full lg:w-[27%] lg:sticky top-0 lg:h-screen lg:overflow-y-auto ${showMobileSummary ? 'hidden lg:flex' : (showPlanSelection ? 'hidden lg:flex' : 'flex')} lg:block flex-col`}>
				<div className="lg:block bg-[#F7F6F2] self-start py-5 px-4 lg:p-0 rounded-[14px] lg:rounded-none lg:h-full w-full">
					{selectedPlan && (
						<div className="flex flex-col lg:py-10 lg:px-8 h-full">
							<div className="hidden lg:block font-founders md:text-2xl/[0.7] lg:text-3xl/[0.9] md:mb-10 lg:mb-12">Your subscription</div>
								<PlanSummary plan={selectedPlan} trialEndDate={trialEndDate} />
								<div className={classNames(
									showPlanSelection ? 'hidden lg:block' : 'block fixed lg:static bottom-0 left-0 right-0 bg-brand-white lg:bg-transparent px-4 pt-4 pb-12 lg:px-0 lg:pt-0 lg:pb-0',
								)}>
									<button
										onClick={() => submitCheckout()}
										className="btn-primary font-founders text-2xl/[0.7] w-full h-12 pt-2 pb-2.5 lg:mb-10 outline-0"
									>
										{changePlanLoading ? (
											<div className="flex items-center justify-center py-1 pointer-events-none">
												<LoaderIcon className="animate-rotation w-6 h-6 text-white" />
											</div>
										) : (
											<span>Go to checkout</span>
										)}
									</button>
								</div>
						</div>
					)}
				</div>
			</div>

			<AnnualUpgradeModal
				modalOpen={annualUpgradeModalOpen}
				setModalOpen={(isOpen) => setAnnualUpgradeModalOpen(isOpen)}
				selectedPlan={selectedPlan}
				plans={plans}
				getMonthlyEquivalent={getMonthlyEquivalent}
				onYearly={() => {
					setPlanPeriod('year');

					if (selectedPlan && plans) {
						const yearlyPlan = plans.find((plan: IMSubscriptionPlan) =>
							plan.period === 'year' && plan.name === selectedPlan.name
						);

						if (yearlyPlan) {
							onRequestCheckoutSession(yearlyPlan)
						}
					}
				}}
				onMonthly={() => onRequestCheckoutSession(selectedPlan)}
			/>
			<ErrorModal
				modalOpen={errorModalOpen}
				setModalOpen={setErrorModalOpen}
				error={errorMessage}
			/>
		</div>
	);
};

export default JoinNew;
